import { Card, CardContent, CardHeader, CardTitle } from '@shadcn/ui';
import { ProjectInsight } from 'app/services/project-services';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Link } from 'react-router-dom';

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload) return null;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
      {payload.map((entry: any, index: number) => (
        <div key={index} className="whitespace-nowrap">
          <span
            style={{
              color: entry.dataKey === 'emissions' ? '#3B82F6' : '#D97706',
              fontWeight: 500,
            }}
          >
            {entry.dataKey === 'emissions' ? 'Emissions' : 'Threshold'}:
          </span>
          <span className="ml-1">{entry.value.toLocaleString()} tCO₂e</span>
        </div>
      ))}
    </div>
  );
};

interface ProjectEmissionsRankingProps {
  projectsWithInsights: ProjectInsight[];
  year: number;
}

export const ProjectEmissionsRanking = ({ projectsWithInsights, year }: ProjectEmissionsRankingProps) => {
  const projectsWithEmissions = projectsWithInsights
    .map((insight) => {
      const yearlyData = insight.insights.formattedMeterReadings.yearlyData?.[year];
      const fullName = insight.project.attributes.name;
      // Remove any text before and including " - " if it exists
      const displayName = fullName.includes(' - ') ? fullName.split(' - ').slice(1).join(' - ').trim() : fullName;

      return {
        id: insight.project.id,
        name: fullName,
        displayName,
        emissions: yearlyData?.actualEmissions || 0,
        penalty: yearlyData?.estimatedAnnualPenalty || 0,
        threshold: yearlyData?.threshold || 0,
      };
    })
    .sort((a, b) => b.emissions - a.emissions);

  // Calculate dynamic height based on number of projects
  // Each project needs ~40px of height, plus margins
  const chartHeight = Math.max(300, projectsWithEmissions.length * 40 + 100);

  // Custom YAxis tick to render links
  const CustomYAxisTick = (props: any) => {
    const { x, y, payload } = props;
    const project = projectsWithEmissions.find((p) => p.name === payload.value);

    return (
      <Link
        to={`/dashboard/project/${project?.id}/building-info`}
        className="text-gray-600 transition-colors duration-150 hover:text-gray-900"
      >
        <text x={x} y={y} dy={4} textAnchor="end" fill="currentColor" className="text-sm">
          {project?.displayName || payload.value}
        </text>
      </Link>
    );
  };

  return (
    <Card className="mt-4 w-full border border-gray-200 bg-white shadow-sm">
      <CardHeader className="border-b border-gray-100 pb-4">
        <CardTitle className="text-center text-lg font-medium text-gray-700">Projects Ranked by Emissions</CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-8">
          {/* Bar chart view */}
          <div style={{ height: `${chartHeight}px` }} className="w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={projectsWithEmissions}
                layout="vertical"
                margin={{ top: 10, right: 30, left: 40, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <XAxis
                  type="number"
                  tickLine={false}
                  axisLine={{ stroke: '#E5E7EB' }}
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  domain={[0, Math.max(...projectsWithEmissions.map((p) => p.emissions))]}
                  label={{
                    value: 'Emissions (tCO₂e)',
                    position: 'bottom',
                    offset: 20,
                    style: {
                      textAnchor: 'middle',
                      fill: '#6B7280',
                      fontSize: 12,
                      fontWeight: 500,
                    },
                  }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tickLine={false}
                  axisLine={{ stroke: '#E5E7EB' }}
                  tick={<CustomYAxisTick />}
                  width={180}
                  interval={0}
                />
                <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} content={<CustomTooltip />} />
                <Bar dataKey="threshold" fill="#D97706" radius={[0, 10, 10, 0]} barSize={10} fillOpacity={0.3} />
                <Bar dataKey="emissions" fill="#3B82F6" radius={[0, 4, 4, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
